// Smart Questionnaire Screen - AI-Driven Adaptive Questions
// Shows only 5-9 targeted questions based on AI preliminary analysis
import { useState, useEffect } from 'react';
import { useApp } from '../hooks/useApp';
import { SCREENS } from '../config/constants';
import AdaptiveQuestionService from '../services/AdaptiveQuestionService';
import Logger from '../services/logger';
import { Container, Text, Card, Button, IconButton, ProgressBar } from '../components/ui';
import Icons from '../components/Icons';

function SmartQuestionnaireScreen() {
    const {
        setCurrentScreen,
        goBack,
        analysisData,
        setAnalysisData,
        setQuestionnaireAnswers,
        showToast
    } = useApp();

    const [questions, setQuestions] = useState([]);
    const [answers, setAnswers] = useState({});
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [verificationContext, setVerificationContext] = useState(null);

    // Generate questions based on preliminary AI analysis
    useEffect(() => {
        if (!analysisData?.preliminary_analysis) {
            Logger.warn('SMART_Q', 'No preliminary analysis found');
            showToast('Không có dữ liệu phân tích', 'error');
            setCurrentScreen(SCREENS.HOME);
            return;
        }

        try {
            const suspectedPatterns = analysisData.preliminary_analysis.suspected_patterns || [];
            Logger.info('SMART_Q', 'Generating verification questions', {
                patternCount: suspectedPatterns.length
            });

            const result = AdaptiveQuestionService.generateVerificationQuestions(
                analysisData.preliminary_analysis
            );

            if (result.skip_verification) {
                // Analysis is confident enough, skip to results
                Logger.info('SMART_Q', 'Skipping verification - confidence high enough');
                setCurrentScreen(SCREENS.RESULTS);
                return;
            }

            const formattedQuestions = AdaptiveQuestionService.formatQuestionsForUI(result.questions);
            setQuestions(formattedQuestions);
            setVerificationContext(result);
            setIsLoading(false);

            Logger.info('SMART_Q', 'Generated questions', {
                count: formattedQuestions.length,
                patterns: result.patterns_to_verify.map(p => p.name)
            });
        } catch (error) {
            Logger.error('SMART_Q', 'Failed to generate questions', { error: error.message });
            showToast('Lỗi tạo câu hỏi', 'error');
            setCurrentScreen(SCREENS.HOME);
        }
    }, [analysisData]);

    const handleAnswer = (questionId, value) => {
        setAnswers(prev => ({ ...prev, [questionId]: value }));
    };

    const handleNext = () => {
        if (currentIndex < questions.length - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    };

    const handlePrev = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    };

    const handleComplete = () => {
        Logger.info('SMART_Q', 'Completing verification', { answerCount: Object.keys(answers).length });

        // Process verification answers and update confidence
        const updatedAnalysis = AdaptiveQuestionService.processVerificationAnswers(
            analysisData.preliminary_analysis,
            answers
        );

        // Store updated analysis and go to final analysis/results
        setQuestionnaireAnswers({
            raw: answers,
            verification: true,
            verification_summary: updatedAnalysis.verification_summary
        });

        // Update analysis data with verification results
        setAnalysisData({
            ...analysisData,
            final_analysis: updatedAnalysis,
            verification_completed: true
        });

        // Go back to analysis to finalize results
        setCurrentScreen(SCREENS.RESULTS);
    };

    const handleSkip = () => {
        Logger.info('SMART_Q', 'Skipping verification');
        setCurrentScreen(SCREENS.RESULTS);
    };

    if (isLoading) {
        return (
            <Container className="min-h-screen bg-theme-bg flex items-center justify-center" safe>
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <Text variant="caption">Đang tạo câu hỏi phù hợp...</Text>
                </div>
            </Container>
        );
    }

    if (questions.length === 0) {
        return (
            <Container className="min-h-screen bg-theme-bg flex items-center justify-center" safe>
                <div className="text-center main-content">
                    <Text variant="h3" className="mb-2">Không cần xác minh</Text>
                    <Text variant="caption" className="mb-6">AI đã có đủ thông tin để phân tích</Text>
                    <Button onClick={() => setCurrentScreen(SCREENS.RESULTS)}>
                        Xem kết quả
                    </Button>
                </div>
            </Container>
        );
    }

    const currentQuestion = questions[currentIndex];
    const progress = ((currentIndex + 1) / questions.length) * 100;
    const isLastQuestion = currentIndex === questions.length - 1;
    const answeredCount = Object.keys(answers).length;

    return (
        <Container className="min-h-screen bg-theme-bg flex flex-col" safe>
            {/* Header */}
            <div className="header-fullwidth border-b border-theme">
                <div className="header-content">
                    <IconButton onClick={goBack}><Icons.Back /></IconButton>
                    <div className="flex-1 mx-3 text-center">
                        <Text className="font-semibold text-sm sm:text-base block">
                            Xác minh nhanh
                        </Text>
                        <Text variant="small" className="text-primary">
                            {currentIndex + 1} / {questions.length} câu hỏi
                        </Text>
                    </div>
                    <button onClick={handleSkip} className="text-primary text-sm font-semibold touch-target">
                        Bỏ qua
                    </button>
                </div>
            </div>

            {/* Progress */}
            <div className="px-responsive pt-4">
                <ProgressBar progress={progress} />
            </div>

            {/* Question Card */}
            <div className="flex-1 overflow-y-auto no-scrollbar pb-32">
                <div className="main-content py-6">
                    {/* Context hint */}
                    {currentQuestion.pattern_name && (
                        <Text variant="small" className="mb-2 text-primary">
                            🔍 Xác minh: {currentQuestion.pattern_name}
                        </Text>
                    )}

                    <Card className="animate-fade-in">
                        <Text className="text-lg font-medium mb-6 block">
                            {currentQuestion.text}
                        </Text>

                        {/* Answer Options */}
                        <div className="option-buttons-grid-5">
                            {currentQuestion.options.map((option) => (
                                <button
                                    key={option.value}
                                    className={`option-btn ${answers[currentQuestion.id] === option.value ? 'selected' : ''}`}
                                    onClick={() => handleAnswer(currentQuestion.id, option.value)}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </Card>

                    {/* Navigation dots */}
                    <div className="flex justify-center gap-2 mt-6">
                        {questions.map((_, idx) => (
                            <button
                                key={idx}
                                onClick={() => setCurrentIndex(idx)}
                                className={`w-2 h-2 rounded-full transition-all ${idx === currentIndex
                                        ? 'w-4 bg-primary'
                                        : answers[questions[idx].id]
                                            ? 'bg-success'
                                            : 'bg-theme-tertiary'
                                    }`}
                            />
                        ))}
                    </div>
                </div>
            </div>

            {/* Footer Navigation */}
            <div className="bottom-nav-wrapper bg-theme-bg border-t border-theme">
                <div className="bottom-nav-inner !border-t-0 p-4">
                    <div className="flex gap-3">
                        {currentIndex > 0 && (
                            <Button variant="secondary" onClick={handlePrev} className="flex-1">
                                ← Trước
                            </Button>
                        )}
                        {isLastQuestion ? (
                            <Button
                                onClick={handleComplete}
                                className="flex-1"
                                disabled={answeredCount === 0}
                            >
                                Hoàn tất ({answeredCount}/{questions.length})
                            </Button>
                        ) : (
                            <Button
                                onClick={handleNext}
                                className="flex-1"
                                variant={answers[currentQuestion.id] ? 'primary' : 'secondary'}
                            >
                                Tiếp →
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </Container>
    );
}

export default SmartQuestionnaireScreen;
